/**
 * The review screen, which is the feature.
 *
 * Inference will sometimes be wrong, and the product's credibility rests entirely
 * on that being obviously correctable. So this screen is built around one column of
 * the table: **how each value will look in a document**. `0.125` becomes `12.5%`,
 * `482310.4` becomes `£482,310.40`, `2026-09-12` becomes `12 September 2026` — and
 * the preview comes from the same formatting functions the renderer uses, so it
 * cannot promise something the PDF will not do.
 *
 * Two things it deliberately does not do.
 *
 * **It does not ask about what it is sure of.** Every column is shown, but only the
 * genuinely uncertain ones are raised — a date column whose day and month order
 * cannot be settled from the data, a column that is text in most rows and a number
 * in the rest. Turning a confident answer into a question is how a two-second
 * upload becomes a five-step wizard.
 *
 * **It does not expose a schema editor.** There is a type, a format and a switch,
 * each named in the vocabulary of a spreadsheet rather than of a type system: not
 * `FieldType`, but "money", "a date", "a percentage".
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { IngestError, plain, previewValue, refreshDataset, type Refreshed, type SourceColumn } from "@broadpaper/ingest";
import { readOffThread } from "../ingest/index.js";
import { api, type DatasetDetail, type DatasetLink, type ReviewColumn } from "../api.js";
import { reportFailure } from "../App.js";
import { changed, useChanges } from "../changes.js";
import { AnotherClick, CONNECTOR_HELP, Cancelled, ConnectorError, PROVIDERS, fetchLinked, pickFile, prepare, type Fetched } from "../connectors/index.js";
import { go } from "../router.js";
import { Button, Chip, Confirm, Empty, Icon, Menu, MenuItem, Note, Sheet, Skeleton, ago, count, lowerDay, toast } from "../ui/index.js";
import { useWorkspace } from "../workspace.js";

/** The types a user may choose, in the words they would use. The data grid offers the same. */
export const TYPES: Array<{ value: SourceColumn["type"]; label: string }> = [
  { value: "string", label: "Text" },
  { value: "number", label: "A number" },
  { value: "currency", label: "Money" },
  { value: "percent", label: "A percentage" },
  { value: "date", label: "A date" },
  { value: "datetime", label: "A date and time" },
  { value: "boolean", label: "Yes or no" },
  { value: "url", label: "A web address" }
];

/** Date patterns offered, written as the result rather than as the pattern. */
const DATE_FORMATS = [
  { value: "d MMMM yyyy", example: "12 September 2026" },
  { value: "d MMM yyyy", example: "12 Sep 2026" },
  { value: "dd/MM/yyyy", example: "12/09/2026" },
  { value: "MMMM yyyy", example: "September 2026" },
  { value: "yyyy-MM-dd", example: "2026-09-12" }
];

/**
 * Every currency ingest can decide a column is in, so the one it chose is always an
 * option here. A column read as rupees, with no INR in the list, showed pounds in
 * the dropdown while it held rupees — and the dropdown is what a person checks.
 */
export const CURRENCIES = ["GBP", "USD", "EUR", "AUD", "BRL", "CAD", "CHF", "DKK", "HKD", "INR", "JPY", "NOK", "NZD", "PLN", "SEK", "SGD", "ZAR"];

/** A linked file read again and merged, and what saving it would change. */
interface Pending {
  merged: Refreshed;
  link: DatasetLink;
  /** The file had more rows than the plan reads. */
  cut: boolean;
  /** The file was chosen again rather than read from where it was. */
  again: boolean;
}

/** Why a refresh did not happen. `next` is set when all it needs is another click. */
interface Trouble {
  message: string;
  hint?: string | undefined;
  lost: boolean;
  next: boolean | null;
}

/** "Amount", "Amount and Due", "Amount, Due and Notes". */
function listed(labels: string[]): string {
  const quoted = labels.map((l) => `“${l}”`);
  return quoted.length > 1 ? `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}` : (quoted[0] ?? "");
}

export function DataReview({ id }: { id: string }) {
  const { view, refresh } = useWorkspace();
  const changes = useChanges();
  /** Whether there are unsaved changes, for a reload that must not throw them away. */
  const edited = useRef(false);
  const [detail, setDetail] = useState<DatasetDetail | null>(null);
  const [columns, setColumns] = useState<ReviewColumn[] | null>(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<{ designs: string[] } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /** Reading the linked file again. */
  const [fetching, setFetching] = useState(false);
  /** A refresh that would change what designs print, waiting to be agreed to. */
  const [pending, setPending] = useState<Pending | null>(null);
  const [applying, setApplying] = useState(false);
  /** Why the last refresh did not happen, kept on screen with what to do about it. */
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const provider = detail?.dataset.link?.provider;
  useEffect(() => {
    // The vendor's scripts, loaded now so that pressing Refresh can open its sign-in
    // window straight away rather than after a download the browser would not wait for.
    if (provider) prepare(provider);
  }, [provider]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await api.dataset(id);
        if (cancelled) return;
        setDetail(loaded);
        // Loaded again when something changed elsewhere — renamed from the sidebar,
        // say — and that must not throw away changes to the columns nobody has saved.
        if (!edited.current) {
          setColumns(loaded.columns);
          setName(loaded.dataset.name);
        }
      } catch (e) {
        if (!cancelled) setFailed(e instanceof Error ? e.message : "We couldn't open this.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, changes]);

  /**
   * Whether anything has actually changed.
   *
   * A save button that is always available on a screen nobody has edited invites a
   * pointless round trip and, worse, makes the user wonder whether they had to
   * press it. Comparing what matters — types, formats, inclusion, the name — is
   * cheap and it means the button says something true.
   */
  const dirty = useMemo(() => {
    if (!detail || !columns) return false;
    if (name.trim() !== detail.dataset.name) return true;
    return columns.some((c, i) => {
      const was = detail.columns[i]!;
      return c.type !== was.type || c.format !== was.format || c.currency !== was.currency || c.include !== was.include;
    });
  }, [detail, columns, name]);

  edited.current = dirty;

  const questions = useMemo(() => (columns ?? []).filter((c) => c.confidence < 0.95 && c.type !== "string" && c.include), [columns]);

  if (failed) {
    return (
      <Empty title="We couldn't open this set of data" actions={<a className="btn btn--primary" href="/data">Back to your data</a>}>
        {failed}
      </Empty>
    );
  }

  if (!detail || !columns) {
    return (
      <div className="wrap stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 40, width: 320 }} />
        <Skeleton rows={1} height={340} />
      </div>
    );
  }

  const update = (index: number, patch: Partial<ReviewColumn>) => {
    setColumns((current) => (current ?? []).map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const link = detail.dataset.link;

  /*
   * Next month's rows, from the file.
   *
   * Read and merged here, in the tab, and then applied straight away when only the rows
   * changed — which is the ordinary month, and deserves no question. A column that has
   * gone, a type the new values no longer fit, a file cut short at the plan's limit, or
   * a different file altogether is shown first, because each changes what a design
   * prints. `again` chooses the file in the picker rather than reading the one kept.
   */
  const refreshFrom = async (again: boolean) => {
    if (!link) return;
    setTrouble(null);
    setFetching(true);
    try {
      const fetched: Fetched | null = again ? await pickFile(link.provider, view.limits.uploadBytes) : await fetchLinked(link, view.limits.uploadBytes);
      if (!fetched) return;
      // Off the page's thread, like bringing a file in: a refresh reads the same fifty
      // thousand rows. The picker, if there was one, has already opened by now.
      const result = await readOffThread({ kind: "bytes", bytes: fetched.bytes, options: { filename: fetched.filename, maxRows: view.limits.rowsPerDataset, maxColumns: 200 } });
      const named = link.sheet ? result.datasets.find((d) => d.label === link.sheet) : undefined;
      // The sheet chosen last time, by name. A newly chosen file falls back to its first
      // sheet; the same file does not, because its first sheet is a different table.
      const sheet = named ?? (link.sheet && !again ? undefined : result.datasets[0]);
      if (!sheet) {
        throw new ConnectorError(
          `The sheet “${link.sheet}” isn't in “${fetched.link.name}” any more.`,
          "If it was renamed, give it its old name back — or choose the file again, and BroadPaper will use its first sheet.",
          true
        );
      }
      if (!sheet.rows.length) throw new ConnectorError(`There are no rows in “${fetched.link.name}” to bring in.`, "Check the rows are still in the file, and that it is the file you meant.");

      const merged = refreshDataset(detail.columns.map(stripPreview), sheet);
      const cut = [...result.notices, ...sheet.notices].some((n) => /larger than BroadPaper reads/i.test(n.message));
      const plan: Pending = { merged, link: { ...fetched.link, ...(result.datasets.length > 1 ? { sheet: sheet.label } : {}) }, cut, again };
      if (again || cut || merged.removed.length || merged.retyped.length) setPending(plan);
      else await apply(plan);
    } catch (e) {
      if (e instanceof Cancelled) return;
      if (e instanceof AnotherClick) setTrouble({ message: e.message, hint: e.hint, lost: false, next: again });
      else if (e instanceof ConnectorError || e instanceof IngestError) setTrouble({ message: e.message, hint: e.hint, lost: e instanceof ConnectorError && e.lost, next: null });
      else reportFailure(e, "We couldn't refresh this.");
    } finally {
      setFetching(false);
    }
  };

  const apply = async (plan: Pending) => {
    const updated = await api.refreshDataset(id, { columns: plan.merged.columns, rows: plan.merged.rows, link: plan.link });
    setDetail(updated);
    setColumns(updated.columns);
    setPending(null);
    changed();
    toast(`Refreshed: ${count(updated.dataset.rowCount)} row${updated.dataset.rowCount === 1 ? "" : "s"} from “${plan.link.name}”.`, "good");
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateDataset(id, { name: name.trim(), columns: columns.map(stripPreview) });
      setDetail(updated);
      setColumns(updated.columns);
      changed();
      toast("Saved.", "good");
    } catch (e) {
      reportFailure(e, "We couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wrap stack" style={{ gap: 22 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">
            <a href="/data" style={{ textDecoration: "none" }}>
              Your data
            </a>{" "}
            · {count(detail.dataset.rowCount)} rows
          </span>
          <input
            className="input input--lg"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Name"
            style={{ fontFamily: "var(--font-display)", fontSize: "1.8rem", border: "1px solid transparent", background: "transparent", padding: "2px 6px", marginLeft: -6, maxWidth: "22ch" }}
          />
          <p className="head__lede">Here is what BroadPaper understood. Change anything that looks wrong — the examples update as you do.</p>
          {link ? (
            <p className="small muted row" style={{ gap: 6, flexWrap: "wrap", margin: 0 }}>
              <Icon name="cloud" />
              From “{link.name}”{link.sheet ? `, sheet “${link.sheet}”,` : ""} in {PROVIDERS[link.provider].name} · refreshed {lowerDay(ago(detail.dataset.refreshedAt))}
            </p>
          ) : null}
        </div>
        <div className="head__actions">
          <Button variant="quiet" icon="trash" onClick={() => setDeleting({ designs: [] })}>
            Delete
          </Button>
          {link ? (
            <>
              {/* Not while columns are unsaved: the refresh would be merged with the
                  columns as they were saved, and the edits on screen would be lost. */}
              <Button
                icon="refresh"
                busy={fetching}
                disabled={dirty}
                title={dirty ? "Save or undo your changes to the columns first" : `Read “${link.name}” again`}
                onClick={() => void refreshFrom(false)}
              >
                Refresh
              </Button>
              <Menu label="The linked file">
                {(close) => (
                  <>
                    <MenuItem
                      onClick={() => {
                        close();
                        void refreshFrom(true);
                      }}
                    >
                      Choose a different file…
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        close();
                        setUnlinking(true);
                      }}
                    >
                      Stop refreshing from it
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : null}
          <Button onClick={() => void save()} busy={saving} disabled={!dirty}>
            {dirty ? "Save changes" : "Saved"}
          </Button>
          {/* The rows themselves, which this screen only shows. A wrong figure in a
              file used to mean bringing the whole file in again. */}
          <a
            className="btn"
            href={`/data/${detail.dataset.id}/edit`}
            onClick={(e) => {
              // Unsaved type changes would be lost on the way, and the grid would open
              // on the types as they were.
              if (dirty && !window.confirm("Leave without saving your changes to the columns?")) e.preventDefault();
            }}
          >
            <Icon name="pencil" /> Edit the data
          </a>
          <a className="btn btn--primary" href={`/designs?data=${detail.dataset.id}`}>
            Make a document <Icon name="chevron" />
          </a>
        </div>
      </div>

      {trouble ? (
        <Note tone={trouble.next !== null ? "ask" : "bad"} title={trouble.message}>
          {trouble.hint ? `${trouble.hint} ` : ""}
          {trouble.next !== null ? (
            <Button size="sm" variant="primary" onClick={() => void refreshFrom(trouble.next!)}>
              Continue
            </Button>
          ) : trouble.lost ? (
            <Button size="sm" variant="primary" onClick={() => void refreshFrom(true)}>
              Choose the file again
            </Button>
          ) : null}{" "}
          <a href={CONNECTOR_HELP} target="_blank" rel="noreferrer">
            What to do about this
          </a>
        </Note>
      ) : null}

      {questions.length ? (
        <Note tone="ask" title={questions.length === 1 ? "One column is worth a look" : `${questions.length} columns are worth a look`}>
          Some values in {questions.map((c) => `“${c.label}”`).join(", ")} do not look like the rest of the column. Check the type below — everything else
          BroadPaper is confident about.
        </Note>
      ) : (
        <Note tone="good" title={`All ${columns.length} columns read cleanly`}>
          BroadPaper worked out every column's type and how it should look. Nothing needs your attention unless you disagree with something.
        </Note>
      )}

      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 44 }}>Use</th>
              <th>Column</th>
              <th style={{ width: 160 }}>What it holds</th>
              <th style={{ width: 190 }}>How it will look</th>
              <th>In your data</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((column, i) => (
              <tr key={column.name} style={column.include ? undefined : { opacity: 0.5 }}>
                <td>
                  <input
                    type="checkbox"
                    checked={column.include}
                    onChange={(e) => update(i, { include: e.target.checked })}
                    aria-label={`Use ${column.label}`}
                    style={{ width: 16, height: 16, accentColor: "var(--red)" }}
                  />
                </td>
                <td>
                  <div className="stack" style={{ gap: 3 }}>
                    <strong>{column.label}</strong>
                    {column.blankRatio > 0.5 ? <span className="small muted">Empty in {Math.round(column.blankRatio * 100)}% of rows</span> : null}
                    {column.constant && column.blankRatio < 1 ? <span className="small muted">The same in every row</span> : null}
                  </div>
                </td>
                <td>
                  <select className="select" value={column.type} onChange={(e) => update(i, { type: e.target.value as SourceColumn["type"], format: undefined })} aria-label={`Type of ${column.label}`}>
                    {TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  {column.confidence < 0.95 && column.type !== "string" ? (
                    <div style={{ marginTop: 6 }}>
                      <Chip tone="ask">Not every value fits</Chip>
                    </div>
                  ) : null}
                </td>
                <td>
                  {/* Only the choices that change what a reader sees. A number's
                      decimal places and a date's pattern do; a boolean's does not. */}
                  {column.type === "date" || column.type === "datetime" ? (
                    <select className="select" value={column.format ?? "d MMMM yyyy"} onChange={(e) => update(i, { format: e.target.value })} aria-label={`Date format of ${column.label}`}>
                      {DATE_FORMATS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.example}
                        </option>
                      ))}
                    </select>
                  ) : column.type === "currency" ? (
                    <select className="select" value={column.currency ?? "GBP"} onChange={(e) => update(i, { currency: e.target.value })} aria-label={`Currency of ${column.label}`}>
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="small muted">{plain(stripPreview(column))}</span>
                  )}
                </td>
                <td>
                  {/*
                    The column that makes this a review. The first value, formatted
                    the way the document will format it, so the user is checking a
                    result rather than a setting.
                  */}
                  <Preview column={column} rows={detail.sampleRows} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="small muted">
        Changing a type re-reads the whole set of data, which is why it takes a moment on a large one. Your designs pick the change up the next time you
        open them.
      </p>

      {/*
        The data itself.
        
        Above this there is one example value per column, which answers "did
        BroadPaper understand my spreadsheet" and not "what is in it". A four
        hundred row file was four hundred rows somebody had to take on trust.
      */}
      <Rows id={detail.dataset.id} total={detail.dataset.rowCount} />

      {deleting ? (
        <Confirm
          title={`Delete “${detail.dataset.name}”?`}
          confirmLabel="Delete it"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              const result = await api.deleteDataset(id);
              await refresh();
              if (result.designsAffected.length) {
                toast(`Deleted. ${result.designsAffected.length} design${result.designsAffected.length === 1 ? "" : "s"} now need data: ${result.designsAffected.join(", ")}.`, "info");
              } else toast("Deleted.", "good");
              go("/data");
            } catch (e) {
              setDeleting(null);
              reportFailure(e, "We couldn't delete that.");
            }
          }}
        >
          The rows go for good. Any design using it keeps its layout and will ask for data again — no design is deleted.
        </Confirm>
      ) : null}

      {pending ? (
        <Sheet
          title={pending.again ? `Bring in the rows from “${pending.link.name}”?` : `Refresh from “${pending.link.name}”?`}
          onClose={() => setPending(null)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setPending(null)}>
                Leave the data as it is
              </Button>
              <Button
                variant="primary"
                busy={applying}
                onClick={async () => {
                  setApplying(true);
                  try {
                    await apply(pending);
                  } catch (e) {
                    reportFailure(e, "We couldn't refresh this.");
                  } finally {
                    setApplying(false);
                  }
                }}
              >
                Bring in {count(pending.merged.rows.length)} row{pending.merged.rows.length === 1 ? "" : "s"}
              </Button>
            </>
          }
        >
          <div className="stack" style={{ gap: 12 }}>
            <p style={{ margin: 0 }}>
              {count(pending.merged.rows.length)} row{pending.merged.rows.length === 1 ? "" : "s"} from the file, replacing the {count(detail.dataset.rowCount)} here now. Any
              rows changed with <em>Edit the data</em> are replaced too.
              {pending.again ? " From now on, Refresh reads this file." : ""}
            </p>
            {pending.cut || pending.merged.removed.length || pending.merged.retyped.length || pending.merged.added.length ? (
              <ul className="notice-list">
                {pending.cut ? (
                  <li>
                    <strong>Your plan holds {count(view.limits.rowsPerDataset)} rows in one set of data, so BroadPaper stopped reading there.</strong> Rows after that point are not included.
                  </li>
                ) : null}
                {pending.merged.removed.length ? (
                  <li>
                    <strong>
                      {listed(pending.merged.removed)} {pending.merged.removed.length === 1 ? "isn't" : "aren't"} in the file any more.
                    </strong>{" "}
                    A design showing {pending.merged.removed.length === 1 ? "it" : "them"} will show a blank there, until you choose something else on its Fields screen.
                  </li>
                ) : null}
                {pending.merged.retyped.length ? (
                  <li>
                    <strong>
                      {listed(pending.merged.retyped)} now {pending.merged.retyped.length === 1 ? "has" : "have"} values that don't fit what {pending.merged.retyped.length === 1 ? "it" : "they"} held,
                    </strong>{" "}
                    so {pending.merged.retyped.length === 1 ? "it is" : "they are"} read as the file has {pending.merged.retyped.length === 1 ? "it" : "them"}. Check the type below afterwards.
                  </li>
                ) : null}
                {pending.merged.added.length ? <li>New in the file: {listed(pending.merged.added)}.</li> : null}
              </ul>
            ) : (
              <p className="small muted" style={{ margin: 0 }}>
                Every column matches the data here, so every design using it goes on finding its columns.
              </p>
            )}
          </div>
        </Sheet>
      ) : null}

      {unlinking && link ? (
        <Confirm
          title={`Stop refreshing from “${link.name}”?`}
          confirmLabel="Stop refreshing"
          onCancel={() => setUnlinking(false)}
          onConfirm={async () => {
            setUnlinking(false);
            try {
              const updated = await api.updateDataset(id, { link: null });
              setDetail(updated);
              changed();
              toast("This data no longer refreshes from the file.", "good");
            } catch (e) {
              reportFailure(e, "We couldn't change that.");
            }
          }}
        >
          The rows stay exactly as they are, and nothing changes in {PROVIDERS[link.provider].name}. To refresh from a file again, bring the data in from it.
        </Confirm>
      ) : null}
    </div>
  );
}

/**
 * A value, as the document will print it.
 *
 * Formatted in the browser rather than asked of the server, so changing a type
 * updates the example as fast as the dropdown closes. It uses the same function the
 * review endpoint uses, which is the same one the renderer uses — a preview
 * produced a different way would eventually promise something the PDF does not do.
 */
function Preview({ column, rows }: { column: ReviewColumn; rows: Array<Record<string, unknown>> }) {
  const [value, formatted] = useMemo(() => {
    const raw = rows.map((r) => r[column.name]).find((v) => v !== null && v !== undefined && v !== "");
    if (raw === undefined) return ["", ""];
    return [String(raw), previewValue(stripPreview(column), raw)];
  }, [column, rows]);

  if (!formatted) return <span className="small muted">No values yet</span>;
  const changed = formatted !== value;
  return (
    <div className="stack" style={{ gap: 2 }}>
      <strong className="num">{formatted}</strong>
      {changed ? (
        <span className="small muted mono" title="The value in your file">
          {value.length > 28 ? `${value.slice(0, 28)}…` : value}
        </span>
      ) : null}
    </div>
  );
}

/** The extra field the API adds for the first render is not part of the column. */
function stripPreview(column: ReviewColumn): SourceColumn {
  const { preview, ...rest } = column;
  void preview;
  return rest;
}

/**
 * Every row, fifty at a time.
 *
 * Paged rather than scrolled, because the question people ask of this is "is my
 * data all here" and a page count answers it in one glance — `1–50 of 412` says
 * more than a scrollbar does. Loaded on demand rather than with the screen: most
 * visits here are about the column types above, and a four hundred row grid nobody
 * opened would be a read of the whole blob for nothing.
 */
function Rows({ id, total }: { id: string; total: number }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<{ columns: Array<{ name: string; label: string }>; rows: Array<Record<string, string>>; offset: number; total: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const size = 50;

  const load = async (offset: number) => {
    setBusy(true);
    try {
      const next = await api.datasetRows(id, offset, size);
      setPage(next);
      setOpen(true);
    } catch (e) {
      reportFailure(e, "We couldn't read your data.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div>
        <Button busy={busy} onClick={() => void load(0)} icon="data">
          Show the {count(total)} row{total === 1 ? "" : "s"}
        </Button>
      </div>
    );
  }

  const shown = page ?? { columns: [], rows: [], offset: 0, total };
  const from = shown.offset + 1;
  const to = Math.min(shown.offset + shown.rows.length, shown.total);

  return (
    <section className="stack" style={{ gap: 10 }}>
      <div className="spread" style={{ flexWrap: "wrap", gap: 10 }}>
        <h2>Your data</h2>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <span className="small muted num">
            {count(from)}–{count(to)} of {count(shown.total)}
          </span>
          <Button size="sm" variant="quiet" icon="back" title="The rows before" disabled={busy || shown.offset === 0} onClick={() => void load(Math.max(0, shown.offset - size))} />
          <Button
            size="sm"
            variant="quiet"
            icon="chevron"
            title="The rows after"
            disabled={busy || to >= shown.total}
            onClick={() => void load(shown.offset + size)}
          />
          <Button size="sm" variant="quiet" onClick={() => setOpen(false)}>
            Hide
          </Button>
        </div>
      </div>
      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              {/* The row's place in the file, so "row 291" in a message means
                  something somebody can find. */}
              <th className="num" style={{ width: 64 }}>
                #
              </th>
              {shown.columns.map((c) => (
                <th key={c.name}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.rows.map((row, i) => (
              <tr key={shown.offset + i}>
                <td className="num small muted">{count(shown.offset + i + 1)}</td>
                {shown.columns.map((c) => (
                  <td key={c.name} className="num">
                    {row[c.name] || <span className="muted">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
